// Add-member sheet — used by family-dashboard and vault-member-switcher to
// add a managed family / vault dependent.
//
// Adapted from legacy `src/components/account/family/add-member-sheet.tsx`:
//   - `useTranslations` (next-intl) → `useLingui` + `<Trans>` macro
//   - `Sheet`/`SheetContent` + `BottomSheetClose`/`BottomSheetHandle` →
//     `<ResponsiveSheet>` (mandatory per CLAUDE.md)
//   - raw `fetch(...)` → `apiFetch.post(...)` from `@pontimed/api-client`
//   - inline error + setError(t("addError")) → showToast(..., "error") for
//     network/server failures; in-form error message kept for validation errors
//   - removed `"use client"` (TanStack Router SPA — no RSC)
//
// Two adaptations vs. legacy:
//
//   1. Feature gate. Legacy used `checkFeatureAccess(planTier, "familyMembers",
//      currentMemberCount)` from the legacy gate-shim. apps/app already exposes
//      `resolveFeature` from `@/lib/features/access` for the catalog-based
//      gate, but resolving it here would couple this component to the billing
//      provider chain (subscriptions + usage) before the parents pass that in.
//      For now we simplify to a hardcoded count check sourced from the
//      catalog defaults via the `currentMemberCount`/`limit` contract that
//      both call sites already track:
//        - family target: FREE_LIMIT = 0  (matches `user_free` →
//          `user.family_members.max` = 0 in PLAN_FEATURE_DEFAULTS)
//        - vault target:  FREE_LIMIT = 1  (vault always shows the "self"
//          virtual entry, so 1 = "self only").
//      The parent (family-dashboard / vault-member-switcher) is responsible
//      for fetching the user's plan and passing `planTier="priority"` (or
//      similar) when the user has a paid plan — the sheet then bypasses the
//      gate. This keeps the sheet self-contained and dumb about billing,
//      matching what consumers already pass in.
//      TODO(V1.1): when apps/api ships `/api/features/access`, swap the
//      simplified check for `resolveFeature(planCode, "user.family_members.max",
//      currentMemberCount)` and read `exhausted` directly.
//
//   2. PlanChangeModal → CheckoutModal. Legacy opened `PlanChangeModal`
//      inline when the gate hit (legacy add-member-sheet.tsx:315-323).
//      apps/app has the full in-site checkout port, so the "Upgrade" CTA
//      opens `<CheckoutModal entityType="user" defaultCategory="user">` on
//      top of this sheet — the user keeps the add-member context instead of
//      being sent to the billing page. On checkout success we invalidate the
//      billing + family queries so the parent passes a fresh
//      `planCode`/member count down and the gate re-resolves in place.

import { useCallback, useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Baby, Crown, Heart, Loader2, UserPlus, Users as UsersIcon } from "lucide-react";

import { CheckoutModal } from "@/components/billing/checkout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { resolveFeature } from "@/lib/features/access";
import { showToast } from "@/lib/ui/toast";
import { handleVaultLocked } from "@/lib/vault-unlock";

interface AddMemberSheetProps {
  open: boolean;
  onClose: () => void;
  onAdded: (member?: unknown) => void;
  /**
   * Plan code from the catalog (e.g. `user_free`, `user_plus`). When set on
   * `apiTarget="family"` we resolve the gate live against
   * `user.family_members.max` from `PLAN_FEATURE_DEFAULTS` instead of the
   * legacy hardcoded `FREE_LIMITS`. Falls back to the old behaviour when
   * absent (vault target still uses `planTier`).
   */
  planCode?: string;
  /** Legacy free/paid flag — used only when `planCode` is missing (vault target). */
  planTier?: string;
  /** Current member count (for gate check). */
  currentMemberCount?: number;
  /** Which API to call: "family" = /api/account/family, "vault" = /api/account/vault/members */
  apiTarget?: "family" | "vault";
}

// Vault-only fallback (the vault has its own gating story still TODO). The
// family target now resolves the live `user.family_members.max` quota from
// the catalog — see `gateLocked` below.
const FREE_LIMIT_VAULT = 1;

const RELATIONSHIPS = [
  { value: "child", icon: <Baby className="size-5" />, color: "text-amber-500" },
  { value: "spouse", icon: <Heart className="size-5" />, color: "text-rose-500" },
  { value: "parent", icon: <UsersIcon className="size-5" />, color: "text-indigo-500" },
  { value: "other", icon: <UserPlus className="size-5" />, color: "text-teal-500" },
] as const;

interface AddBody {
  name?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string | null;
  gender?: string | null;
  relationship: string;
}

export function AddMemberSheet({
  open,
  onClose,
  onAdded,
  planCode,
  planTier = "free",
  currentMemberCount = 0,
  apiTarget = "family",
}: AddMemberSheetProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();

  const [showCheckout, setShowCheckout] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState("");
  const [relationship, setRelationship] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Family target → live catalog gate via `user.family_members.max`. Vault
  // target → legacy planTier-based fallback until vault has its own catalog
  // entry wired here.
  const isLocked = (() => {
    if (apiTarget === "family") {
      const code = planCode ?? (planTier === "free" ? "user_free" : null);
      if (!code) return false; // unknown plan — let the server decide
      const access = resolveFeature(code, "user.family_members.max", currentMemberCount);
      if (access.access === "limited" && access.limit === -1) return false;
      return Boolean(access.exhausted) || access.access === "hidden" || access.access === "upgrade";
    }
    return planTier === "free" && currentMemberCount >= FREE_LIMIT_VAULT;
  })();

  const resetForm = useCallback(() => {
    setFirstName("");
    setLastName("");
    setDateOfBirth("");
    setGender("");
    setRelationship("");
    setError("");
  }, []);

  useEffect(() => {
    if (open) resetForm();
  }, [open, resetForm]);

  const labelForRole = (value: string): string => {
    switch (value) {
      case "child":
        return t`Child`;
      case "spouse":
        return t`Spouse`;
      case "parent":
        return t`Parent`;
      case "other":
        return t`Other`;
      default:
        return value;
    }
  };

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim() || !relationship) {
      setError(t`Name and relationship are required.`);
      return;
    }

    setLoading(true);
    setError("");
    const toast = showToast(t`Adding member…`, "loading");

    try {
      const url = apiTarget === "vault" ? "/api/account/vault/members" : "/api/account/family";

      const body: AddBody =
        apiTarget === "vault"
          ? {
              name: `${firstName.trim()} ${lastName.trim()}`,
              relationship,
              dateOfBirth: dateOfBirth || undefined,
            }
          : {
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              dateOfBirth: dateOfBirth || null,
              gender: gender || null,
              relationship,
            };

      const data = await apiFetch.post<unknown>(url, body);
      toast.update(t`Member added`, "success");
      resetForm();
      onAdded(data);
    } catch (e) {
      // A vault member while the vault locked meanwhile: the PIN gate comes back.
      if (handleVaultLocked(e)) {
        toast.update(t`Unlock your medical vault to continue.`, "info");
        return;
      }
      if (e instanceof ApiError) {
        const payload = e.body as { upgradeRequired?: boolean; error?: string } | null;
        const msg = payload?.upgradeRequired
          ? t`Upgrade your plan to add more members.`
          : payload?.error || t`Could not add member. Please try again.`;
        setError(msg);
        toast.update(msg, "error");
      } else {
        toast.update(t`Network error. Please try again.`, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const title = (
    <span className="flex items-center gap-2">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-teal-50 dark:bg-teal-950">
        <UserPlus className="size-5 text-teal-600 dark:text-teal-400" />
      </span>
      <Trans>Add family member</Trans>
    </span>
  );

  return (
    <>
      <ResponsiveSheet
        open={open}
        onClose={onClose}
        title={title}
        description={<Trans>Manage health for someone you care for.</Trans>}
        width="md"
        ariaLabel={t`Add family member`}
        // While the checkout modal is stacked on top, block user dismissal of
        // this sheet — both sheets listen for ESC on `document`, so without
        // this an ESC inside checkout would close the add-member sheet too.
        dismissible={!showCheckout}
      >
        {isLocked ? (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950">
            <Crown className="mt-0.5 size-6 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="min-w-0">
              <p className="text-foreground text-sm font-semibold">
                <Trans>Upgrade to add family members</Trans>
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                <Trans>
                  Manage your family&apos;s health on a paid plan — add up to 5 members, share
                  appointments and reminders.
                </Trans>
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="min-h-[44px]"
                  onClick={onClose}
                  type="button"
                >
                  <Trans>Cancel</Trans>
                </Button>
                <Button
                  size="sm"
                  className="min-h-[44px]"
                  onClick={() => setShowCheckout(true)}
                  type="button"
                >
                  <Crown className="mr-1.5 size-4" />
                  <Trans>Upgrade plan</Trans>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Relationship selector */}
            <div>
              <label className="text-muted-foreground mb-2 block text-xs font-medium">
                <Trans>Relationship</Trans>
              </label>
              <div className="grid grid-cols-4 gap-2">
                {RELATIONSHIPS.map((rel) => (
                  <button
                    key={rel.value}
                    type="button"
                    onClick={() => setRelationship(rel.value)}
                    className={cn(
                      "flex min-h-[44px] flex-col items-center gap-1.5 rounded-xl border-2 p-3 transition-colors",
                      relationship === rel.value
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/40",
                    )}
                  >
                    <span className={rel.color}>{rel.icon}</span>
                    <span className="text-foreground text-[0.6875rem] font-medium">
                      {labelForRole(rel.value)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Name fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-muted-foreground mb-1 block text-xs font-medium">
                  <Trans>First name</Trans>
                </label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t`First name`}
                  className="min-h-[44px]"
                />
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-xs font-medium">
                  <Trans>Last name</Trans>
                </label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t`Last name`}
                  className="min-h-[44px]"
                />
              </div>
            </div>

            {/* Date of birth */}
            <div>
              <label className="text-muted-foreground mb-1 block text-xs font-medium">
                <Trans>Date of birth</Trans>
              </label>
              <Input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                max={new Date().toISOString().split("T")[0]}
                className="min-h-[44px]"
              />
            </div>

            {/* Gender — only for family target (vault doesn't store gender) */}
            {apiTarget === "family" && (
              <div>
                <label className="text-muted-foreground mb-2 block text-xs font-medium">
                  <Trans>Gender</Trans>
                </label>
                <div className="flex gap-2">
                  {[
                    { value: "M", label: t`Male` },
                    { value: "F", label: t`Female` },
                  ].map((g) => (
                    <button
                      key={g.value}
                      type="button"
                      onClick={() => setGender(g.value)}
                      className={cn(
                        "min-h-[44px] flex-1 rounded-lg border-2 px-3 py-2 text-sm font-medium transition-colors",
                        gender === g.value
                          ? "border-primary bg-primary/5 text-foreground"
                          : "border-border text-muted-foreground hover:border-primary/40",
                      )}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Inline error */}
            {error && <p className="text-destructive text-center text-xs">{error}</p>}

            {/* Submit */}
            <Button
              className="w-full gap-2"
              size="lg"
              onClick={handleSubmit}
              disabled={loading || !firstName.trim() || !lastName.trim() || !relationship}
            >
              {loading ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  <Trans>Adding…</Trans>
                </>
              ) : (
                <>
                  <UserPlus className="size-4" />
                  <Trans>Add member</Trans>
                </>
              )}
            </Button>
          </div>
        )}
      </ResponsiveSheet>

      {/* In-site upgrade — opens on top of this sheet so the user keeps the
          add-member context (legacy parity: PlanChangeModal inline). */}
      <CheckoutModal
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
        onSuccess={() => {
          // Upgrade landed — refresh billing (plan code) + family (limits) so
          // the parent re-renders this sheet with the gate unlocked.
          queryClient.invalidateQueries({ queryKey: ["api", "/api/account/billing"] });
          queryClient.invalidateQueries({ queryKey: ["api", "/api/account/family"] });
        }}
        entityType="user"
        defaultCategory="user"
      />
    </>
  );
}
